import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import language from '../../variables/language';

const DEBOUNCE_MS = 300;

// the words dictionary has 5000+ rows - unlike ParticipantSearchSelect/
// ConversationTemplatePicker (which fetch everything once), this genuinely
// searches server-side on every keystroke, debounced.
export default function WordSearchSelect({ value, onChange, placeholder, onNoMatchChange }) {
   const [results, setResults] = useState([]);
   const [selected, setSelected] = useState(null);
   const [loading, setLoading] = useState(false);
   const [query, setQuery] = useState('');
   const [open, setOpen] = useState(false);
   const wrapRef = useRef(null);
   const debounceRef = useRef(null);
   const lang = language[language.lang];

   useEffect(() => {
      if(!value) {
         setSelected(null);
         setQuery('');
      }
   }, [value]);

   useEffect(() => {
      if(!open) {
         return;
      }
      if(!query.trim()) {
         setResults([]);
         return;
      }
      setLoading(true);
      if(debounceRef.current) {
         clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
         axios.get('/api/words?search=' + encodeURIComponent(query.trim()), { withCredentials: true })
            .then(response => setResults(Array.isArray(response.data) ? response.data : []))
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
      }, DEBOUNCE_MS);
      return () => { if(debounceRef.current) clearTimeout(debounceRef.current); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [query, open]);

   // Lets a caller (e.g. LessonDetailModal's quick "+AI" word-create button)
   // know when the typed text has been searched and genuinely matched
   // nothing, as opposed to "still typing"/"still loading"/"already picked".
   useEffect(() => {
      if(!onNoMatchChange) {
         return;
      }
      const trimmed = query.trim();
      const noMatch = !value && !loading && trimmed.length > 0 && results.length === 0;
      onNoMatchChange(noMatch, trimmed);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [value, loading, query, results]);

   useEffect(() => {
      const handleOutside = (e) => {
         if(wrapRef.current && !wrapRef.current.contains(e.target)) {
            setOpen(false);
         }
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   const primaryTranslation = (w) => (w.translations && w.translations.length ? w.translations[0].translation : '');

   const displayLabel = (w) => {
      const primary = primaryTranslation(w);
      return w.word + (primary ? ' · ' + primary : '');
   }

   const handlePick = (word) => {
      onChange(String(word.id));
      setSelected(word);
      setQuery(displayLabel(word));
      setOpen(false);
   }

   const handleInputChange = (e) => {
      setQuery(e.target.value);
      if(value) {
         onChange('');
      }
   }

   const displayValue = open ? query : (selected ? displayLabel(selected) : query);

   return (
      <div className='word-search-select' ref={wrapRef}>
         <input className='submit-field from-input txt-c word-search-input'
            type='text' placeholder={placeholder}
            value={displayValue}
            onFocus={() => setOpen(true)}
            onChange={handleInputChange} />
         {open ? (
            <div className='word-search-dropdown'>
               {loading ? <div className='word-search-empty'>{lang['Loading ...']}</div> : ''}
               {!loading && !query.trim() ? <div className='word-search-hint'>{lang.wordsTab.searchHint}</div> : ''}
               {!loading && query.trim() && results.length === 0 ? <div className='word-search-empty'>{lang['Empty list']}</div> : ''}
               {!loading ? results.map(w => (
                  <div key={w.id} className='word-search-option' onClick={() => handlePick(w)}>
                     <span className='word-search-option-word'>{w.word}</span>
                     {primaryTranslation(w) ? <span className='word-search-option-translation'>{primaryTranslation(w)}</span> : ''}
                     {w.partOfSpeech ? <span className='word-search-option-pos'>{w.partOfSpeech.code}</span> : ''}
                  </div>
               )) : ''}
            </div>
         ) : ''}
      </div>
   );
}
